import type React from "react";

import { useAudioList } from "@/app/_providers/audio-list";
import { type MouseEvent, useCallback, useRef, useState } from "react";
import { useEffect } from "react";
import WaveSurfer from "wavesurfer.js";
import { cancelIdleCallbackWithFallback, requestIdleCallbackWithFallback } from "@utilities/requestIdle";

export const useWavesurfer = ({
  url,
  id,
  timelineRef,
  height = 32,
  waveColor = "#949494",
  progressColor = "#030712",
  onRender,
  waveform,
  audioDuration,
  resolvePlaybackUrl,
  onRateLimited,
  onWaveformInteraction,
}: {
  height?: number;
  id: string;
  progressColor?: string;
  timelineRef: React.RefObject<HTMLDivElement | null>;
  url: string;
  waveColor?: string;
  onRender?: () => void;
  /** Pre-computed waveform data (e.g. from API visual_waveform). When provided, audio is not fetched until play. */
  waveform?: number[];
  /** Pre-known duration in seconds (e.g. from API audio_duration_seconds). */
  audioDuration?: number;
  /** Optional: resolve URL before loading (e.g. add auth token). Used when lazy-loading. */
  resolvePlaybackUrl?: (url: string) => Promise<string>;
  /** Called when the server responds with HTTP 429 (rate limited). */
  onRateLimited?: () => void;
  /** Called once per direct waveform seek interaction. */
  onWaveformInteraction?: (progress: number) => void;
}) => {
  const [duration, setDuration] = useState(audioDuration ?? 0);
  const [mediaElement, setMediaElement] = useState<HTMLMediaElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const keyboardContainerRef = useRef<HTMLDivElement>(null);
  const audioLoadedRef = useRef(!waveform?.length);
  const shouldAutoPlayRef = useRef(false);
  const onRateLimitedRef = useRef(onRateLimited);
  onRateLimitedRef.current = onRateLimited;
  /** True only after we called load(url) in lazy mode; used to ignore the initial decode from create(peaks, duration). */
  const loadRequestedRef = useRef(false);
  /** Called when we need to load audio in lazy mode; resolves URL then loads. */
  const triggerLazyLoadRef = useRef<() => void>(() => {});
  /** Tracks the blob URL created for lazy-loaded audio so it can be revoked. */
  const blobUrlRef = useRef<string | null>(null);
  const { setPlayingItemId, playingItemId } = useAudioList();
  const isPlaying = playingItemId === id;
  const lazyLoad = Boolean(waveform?.length);

  const captureWaveformInteraction = useCallback(() => {
    const duration = wavesurferRef.current?.getDuration() ?? 0;
    if (duration <= 0) return;

    onWaveformInteraction?.((wavesurferRef.current?.getCurrentTime() ?? 0) / duration);
  }, [onWaveformInteraction]);

  useEffect(() => {
    audioLoadedRef.current = !lazyLoad;
    loadRequestedRef.current = false;
    triggerLazyLoadRef.current = () => {
      shouldAutoPlayRef.current = true;
      setIsLoading(true);
      (async () => {
        try {
          const finalUrl = resolvePlaybackUrl ? await resolvePlaybackUrl(url) : url;
          const response = await fetch(finalUrl);
          if (response.status === 429) {
            setIsLoading(false);
            shouldAutoPlayRef.current = false;
            setPlayingItemId(null);
            onRateLimitedRef.current?.();
            return;
          }
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const blob = await response.blob();
          if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
          }
          const blobUrl = URL.createObjectURL(blob);
          blobUrlRef.current = blobUrl;
          loadRequestedRef.current = true;
          wavesurferRef.current?.load(blobUrl);
        } catch {
          setIsLoading(false);
          loadRequestedRef.current = false;
          shouldAutoPlayRef.current = false;
          setPlayingItemId(null);
        }
      })();
    };
    const initializeWavesurfer = () => {
      if (!timelineRef.current) return;

      // Cleanup existing instance if any
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
        setMediaElement(null);
        setDuration(lazyLoad ? (audioDuration ?? 0) : 0);
      }

      const barOptions = {
        barWidth: 3,
        barGap: 3,
        barRadius: 3,
        barMinHeight: 3,
      };

      const createOptions = {
        container: timelineRef.current,
        waveColor,
        progressColor,
        cursorColor: "rgba(0,0,0,0)",
        height,
        dragToSeek: true,
        ...(lazyLoad && waveform?.length
          ? { peaks: [waveform] as (Float32Array | number[])[], duration: audioDuration ?? 0 }
          : { url }),
        renderFunction(channelData: (Float32Array | number[])[], ctx: CanvasRenderingContext2D) {
          const topChannel = channelData[0];
          const bottomChannel = channelData[1] || channelData[0];
          const length = topChannel.length;

          const { width, height } = ctx.canvas;
          const halfHeight = height / 2;
          const pixelRatio = window.devicePixelRatio;

          const barWidth = barOptions.barWidth ? barOptions.barWidth * pixelRatio : 1;
          const barGap = barOptions.barGap ? barOptions.barGap * pixelRatio : barOptions.barWidth ? barWidth / 2 : 0;
          const barRadius = barOptions.barRadius || 0;
          const barIndexScale = width / (barWidth + barGap) / length;

          const rectFn = barRadius && "roundRect" in ctx ? "roundRect" : "rect";

          const firstChannel = channelData[0];
          const max = Array.from(firstChannel).reduce(
            (acc: number, value: number) => Math.max(acc, Math.abs(value)),
            0,
          );
          const vScale = max ? 1 / max : 1;

          ctx.beginPath();

          let prevX = 0;
          let maxTop = 0;
          let maxBottom = 0;
          for (let i = 0; i <= length; i++) {
            const x = Math.round(i * barIndexScale);

            if (x > prevX) {
              const topBarHeight = Math.round(maxTop * halfHeight * pixelRatio);
              const bottomBarHeight = Math.round(maxBottom * halfHeight * pixelRatio);
              const barHeight = Math.max(
                ((topBarHeight + bottomBarHeight) * vScale) / 2,
                barOptions.barMinHeight * 2,
                1,
              );

              // Vertical alignment
              const y = halfHeight - barHeight / 2;

              ctx[rectFn](prevX * (barWidth + barGap), y, barWidth, barHeight, barRadius);

              prevX = x;
              maxTop = 0;
              maxBottom = 0;
            }

            const magnitudeTop = Math.abs(topChannel[i] || 0);
            const magnitudeBottom = Math.abs(bottomChannel[i] || 0);
            if (magnitudeTop > maxTop) maxTop = magnitudeTop;
            if (magnitudeBottom > maxBottom) maxBottom = magnitudeBottom;
          }

          ctx.fill();
          ctx.closePath();
          onRender?.();
        },
      };
      wavesurferRef.current = WaveSurfer.create(createOptions);

      wavesurferRef.current.on("decode", () => {
        const d = wavesurferRef.current?.getDuration?.() ?? 0;
        setDuration(d);
        // Only run lazy-load completion when we requested load(url); ignore initial decode from create(peaks, duration).
        if (lazyLoad && loadRequestedRef.current) {
          loadRequestedRef.current = false;
          audioLoadedRef.current = true;
          setIsLoading(false);
          setMediaElement(wavesurferRef.current?.getMediaElement() ?? null);
          if (shouldAutoPlayRef.current) {
            shouldAutoPlayRef.current = false;
            wavesurferRef.current?.play();
            setPlayingItemId(id);
            keyboardContainerRef.current?.focus();
          }
        }
      });
      wavesurferRef.current.on("dragstart", () => {
        wavesurferRef.current?.pause();
      });
      wavesurferRef.current.on("click", () => {
        captureWaveformInteraction();
        if (lazyLoad && !audioLoadedRef.current) {
          triggerLazyLoadRef.current();
        } else {
          wavesurferRef.current?.play();
          setPlayingItemId(id);
          keyboardContainerRef.current?.focus();
        }
      });
      wavesurferRef.current.on("dragend", () => {
        captureWaveformInteraction();
        if (lazyLoad && !audioLoadedRef.current) {
          triggerLazyLoadRef.current();
        } else {
          setTimeout(() => {
            wavesurferRef.current?.play();
            setPlayingItemId(id);
            keyboardContainerRef.current?.focus();
          }, 500);
        }
      });
      wavesurferRef.current.on("finish", () => {
        wavesurferRef.current?.seekTo(0);
        setPlayingItemId(null);
      });
      if (!lazyLoad) {
        setMediaElement(wavesurferRef.current.getMediaElement());
      }
    };

    const idleCallbackId = requestIdleCallbackWithFallback(
      () => {
        initializeWavesurfer();
      },
      { timeout: 400 },
    );

    // Cleanup function
    return () => {
      cancelIdleCallbackWithFallback(idleCallbackId);
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
        setMediaElement(null);
        setDuration(lazyLoad ? (audioDuration ?? 0) : 0);
      }
    };
  }, [
    url,
    id,
    timelineRef,
    setPlayingItemId,
    onRender,
    lazyLoad,
    waveform,
    audioDuration,
    resolvePlaybackUrl,
    captureWaveformInteraction,
  ]);

  // Sync audio-list context with actual media element playback state
  // This handles the case when play() is called externally (e.g., from TranscriptView)
  useEffect(() => {
    if (!mediaElement) return;

    const handlePlay = () => setPlayingItemId(id);
    const handlePauseOrEnd = () => setPlayingItemId(prev => (prev === id ? null : prev));

    mediaElement.addEventListener("play", handlePlay);
    mediaElement.addEventListener("pause", handlePauseOrEnd);
    mediaElement.addEventListener("ended", handlePauseOrEnd);

    return () => {
      mediaElement.removeEventListener("play", handlePlay);
      mediaElement.removeEventListener("pause", handlePauseOrEnd);
      mediaElement.removeEventListener("ended", handlePauseOrEnd);
    };
  }, [id, mediaElement, setPlayingItemId]);

  const togglePlayPause = useCallback(
    (event?: MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation();

      if (!isPlaying) {
        setPlayingItemId(id);
      } else {
        setPlayingItemId(null);
      }
    },
    [isPlaying, id, setPlayingItemId],
  );

  const play = useCallback(
    (explicitId?: string) => {
      setPlayingItemId(explicitId || id);
    },
    [id, setPlayingItemId],
  );

  useEffect(() => {
    // when another item start playing, reset timer
    if (playingItemId !== null && id !== playingItemId) {
      wavesurferRef.current?.seekTo(0);
    }

    const handler = (event: KeyboardEvent) => {
      const activeElement = document.activeElement as HTMLElement;

      // Check if focus is within this hook's container
      const hasKeyboardFocus = keyboardContainerRef.current?.contains(activeElement);

      // Only handle spacebar if focused within container and audio has started
      if (hasKeyboardFocus && wavesurferRef.current?.getCurrentTime() !== 0 && event.code === "Space") {
        event.preventDefault();
        togglePlayPause();
      }
    };

    document.addEventListener("keydown", handler);

    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [playingItemId, togglePlayPause, id]);

  useEffect(() => {
    if (isPlaying) {
      if (lazyLoad && !audioLoadedRef.current) {
        triggerLazyLoadRef.current();
      } else {
        wavesurferRef.current?.play();
        keyboardContainerRef.current?.focus();
      }
    } else {
      shouldAutoPlayRef.current = false;
      wavesurferRef.current?.pause();
    }
  }, [isPlaying, setPlayingItemId, lazyLoad]);

  const seekTo = useCallback(
    (time: number) => {
      wavesurferRef.current?.seekTo(time / duration);
    },
    [duration],
  );

  return { duration, isPlaying, togglePlayPause, play, seekTo, mediaElement, keyboardContainerRef, isLoading };
};
